import React from 'react';
import { Box, Container, Typography, Breadcrumbs, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

interface PageHeroProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: { label: string; path?: string }[];
  accentColor?: string;
  /** When true: full-width centered title, fixed height — matches the department detail hero style */
  centered?: boolean;
}

const BUILDING_PATTERN = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='80'%3E%3Crect x='10' y='30' width='40' height='50' fill='white'/%3E%3Crect x='15' y='20' width='30' height='12' fill='white'/%3E%3Crect x='27' y='10' width='6' height='12' fill='white'/%3E%3Crect x='55' y='40' width='55' height='40' fill='white'/%3E%3Crect x='65' y='28' width='35' height='14' fill='white'/%3E%3Crect x='78' y='18' width='9' height='12' fill='white'/%3E%3Crect x='15' y='42' width='8' height='10' fill='%230a1628'/%3E%3Crect x='27' y='42' width='8' height='10' fill='%230a1628'/%3E%3Crect x='62' y='50' width='10' height='12' fill='%230a1628'/%3E%3Crect x='78' y='50' width='10' height='12' fill='%230a1628'/%3E%3C/svg%3E")`;

const PageHero: React.FC<PageHeroProps> = ({
  title,
  subtitle,
  breadcrumbs,
  accentColor = '#0B4F8A',
  centered = true,
}) => {
  if (centered) {
    // ── Centered hero (matches DepartmentDetailPage style) ──────────────────
    return (
      <Box
        sx={{
          position: 'relative',
          height: { xs: 200, md: 280 },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(160deg, ${accentColor}CC 0%, ${accentColor}99 100%)`,
        }}
      >
        {/* Building silhouette texture */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.06,
            backgroundImage: BUILDING_PATTERN,
            backgroundRepeat: 'repeat',
            backgroundSize: '200px 140px',
            pointerEvents: 'none',
          }}
        />
        {/* Soft white highlight — top right */}
        <Box sx={{ position: 'absolute', top: 0, right: 0, width: '50%', height: '100%', background: 'radial-gradient(ellipse at top right, rgba(255,255,255,0.12) 0%, transparent 65%)', pointerEvents: 'none' }} />

        {/* Breadcrumbs */}
        {breadcrumbs && (
          <Breadcrumbs
            separator={<NavigateNextIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />}
            sx={{ position: 'relative', mb: 1.5 }}
          >
            {breadcrumbs.map((crumb, i) =>
              crumb.path ? (
                <Link key={i} component={RouterLink} to={crumb.path} underline="hover"
                  sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem' }}>
                  {crumb.label}
                </Link>
              ) : (
                <Typography key={i} sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                  {crumb.label}
                </Typography>
              )
            )}
          </Breadcrumbs>
        )}

        {/* Title */}
        <Typography
          variant="h2"
          sx={{
            position: 'relative',
            color: 'white',
            fontWeight: 700,
            textAlign: 'center',
            fontSize: { xs: '1.8rem', md: '3rem' },
            letterSpacing: '-0.5px',
            textShadow: '0 2px 12px rgba(0,0,0,0.5)',
            px: 3,
          }}
        >
          {title}
        </Typography>

        {/* Subtitle */}
        {subtitle && (
          <Typography
            sx={{
              position: 'relative',
              color: 'rgba(255,255,255,0.82)',
              fontSize: { xs: '0.9rem', md: '1.05rem' },
              textAlign: 'center',
              mt: 1.5,
              maxWidth: 680,
              px: 3,
              lineHeight: 1.65,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    );
  }

  // ── Left-aligned hero (legacy fallback) ────────────────────────────────────
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 7, md: 10 },
        color: 'white',
        background: `linear-gradient(160deg, ${accentColor}EE 0%, ${accentColor}99 45%, #061224 100%)`,
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -1,
          left: 0,
          right: 0,
          height: 40,
          background: 'linear-gradient(to bottom right, transparent 49%, var(--bg-color, #F8FAFC) 50%)',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.07,
          backgroundImage: BUILDING_PATTERN,
          backgroundRepeat: 'repeat',
          backgroundSize: '220px 150px',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0, right: 0, bottom: 0,
          width: '45%',
          background: 'radial-gradient(ellipse at right center, rgba(255,255,255,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <Container maxWidth="xl" sx={{ position: 'relative' }}>
        {breadcrumbs && (
          <Breadcrumbs
            separator={<NavigateNextIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }} />}
            sx={{ mb: 2.5 }}
          >
            {breadcrumbs.map((crumb, i) =>
              crumb.path ? (
                <Link key={i} component={RouterLink} to={crumb.path} underline="hover"
                  sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem' }}>
                  {crumb.label}
                </Link>
              ) : (
                <Typography key={i} sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.82rem' }}>
                  {crumb.label}
                </Typography>
              )
            )}
          </Breadcrumbs>
        )}
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '1.9rem', md: '2.8rem' },
            fontWeight: 800,
            mb: subtitle ? 1.5 : 0,
            letterSpacing: '-0.5px',
            textShadow: '0 2px 16px rgba(0,0,0,0.4)',
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="h6"
            sx={{ color: 'rgba(255,255,255,0.82)', fontWeight: 400, maxWidth: 680, fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.7 }}
          >
            {subtitle}
          </Typography>
        )}
      </Container>
    </Box>
  );
};

export default PageHero;
